/**
 * Chat Message Bubble
 *
 * Individual message display with user/assistant styling
 */

import { Typography, Avatar } from 'antd';
import { UserOutlined, RobotOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { ChatMessage } from '../../types/ai';
import useThemeStore from '../../store/themeStore';

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;

interface ChatMessageBubbleProps {
  message: ChatMessage;
}

export default function ChatMessageBubble({ message }: ChatMessageBubbleProps) {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const isUser = message.role === 'user';

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: 16,
        gap: 8,
      }}
    >
      {!isUser && (
        <Avatar
          size="small"
          icon={<RobotOutlined />}
          style={{ backgroundColor: '#1890ff', flexShrink: 0 }}
        />
      )}

      <div
        style={{
          maxWidth: '75%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
        }}
      >
        <div
          style={{
            background: isUser ? '#1890ff' : (isDark ? '#1f1f1f' : '#f0f0f0'),
            color: isUser ? '#fff' : (isDark ? 'rgba(255, 255, 255, 0.85)' : '#000'),
            padding: '8px 12px',
            borderRadius: 12,
            borderTopRightRadius: isUser ? 4 : 12,
            borderTopLeftRadius: isUser ? 12 : 4,
            wordBreak: 'break-word',
          }}
        >
          <Paragraph
            style={{
              margin: 0,
              color: isUser ? '#fff' : (isDark ? 'rgba(255, 255, 255, 0.85)' : '#000'),
              whiteSpace: 'pre-wrap',
            }}
          >
            {message.content}
          </Paragraph>
        </div>

        <Text
          type="secondary"
          style={{
            fontSize: 11,
            marginTop: 4,
            opacity: 0.7,
          }}
        >
          {dayjs(message.timestamp).fromNow()}
        </Text>
      </div>

      {isUser && (
        <Avatar
          size="small"
          icon={<UserOutlined />}
          style={{ backgroundColor: '#52c41a', flexShrink: 0 }}
        />
      )}
    </div>
  );
}
